import { cond, equals } from 'ramda';
import React, { ChangeEventHandler, FC, useRef } from 'react';
import { useDeepCompareEffect } from 'react-use';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import FlexColumn from '@ui/atoms/FlexColumn';
import SubheadingText from '@ui/atoms/SubheadingText';

import { ColorTypes } from '@folx-types/theme';

import theme from '@theme/FolxTheme';

interface ITextAreaProps {
	className?: string;
	disabled?: boolean;
	errorLabel?: string | boolean;
	label?: string;
	name?: string;
	onChange?: ChangeEventHandler<HTMLTextAreaElement>;
	placeholder?: string;
	value?: string;
	labelVariant?: 'aria' | 'normal' | 'heading';
	id?: string;
}

const ErrorBox = styled.div`
	color: ${({ theme: components }) => components.textInput.errorColor};
	font-size: ${({ theme: components }) => components.textInput.errorFontSize};
	line-height: ${({ theme: components }) => components.textInput.errorLineHeight};
`;

const TextArea: FC<ITextAreaProps> = ({
	placeholder,
	className,
	errorLabel = '',
	label = '',
	name = '',
	onChange = () => {},
	disabled = false,
	value,
	labelVariant = 'normal',
	id,
}) => {
	const ref = useRef(null);
	const textId = id ?? `${name}Id`;

	const resizeInput = () => {
		if (ref.current) {
			ref.current.style.height = 'auto';
			ref.current.style.height = `calc(${ref.current.scrollHeight}px + ${theme.spacing.xxs})`;
		}
	};

	const handleInput = () => {
		resizeInput();
	};

	useDeepCompareEffect(() => {
		resizeInput();
	}, []);

	const input = (
		<textarea
			ref={ref}
			className={className}
			name={name}
			placeholder={placeholder}
			onChange={onChange}
			onInput={handleInput}
			disabled={disabled}
			value={value}
			id={textId}
		/>
	);

	const heading = cond([
		[equals('heading'), () => <SubheadingText>{label}</SubheadingText>],
		[equals('normal'), () => <BasicText variant="field">{label}</BasicText>],
	])(labelVariant);

	return labelVariant !== 'aria' ? (
		<FlexColumn gap="xs">
			<label htmlFor={textId}>{heading}</label>
			{input}
			{errorLabel && <ErrorBox>{errorLabel}</ErrorBox>}
		</FlexColumn>
	) : (
		<FlexColumn gap="xs">
			<label htmlFor={textId} aria-label={label}>
				{input}
			</label>
			{errorLabel && <ErrorBox>{errorLabel}</ErrorBox>}
		</FlexColumn>
	);
};

TextArea.displayName = 'TextArea';
export default styled(TextArea)<{
	border?: boolean;
	error?: boolean;
	borderColor?: ColorTypes;
}>`
	border: 1px solid ${theme.colors.transparent};
	border-radius: ${theme.radius.md};
	box-sizing: border-box;
	font-family: Chalet-LondonSixty, sans-serif;
	font-size: ${theme.fontSizes.body.sm};
	line-height: ${theme.lineHeights.body.md};
	min-height: 8rem;
	outline: ${({ error }) =>
		error
			? `1px solid ${theme.tints.darken40(theme.colors.red)}`
			: `1px solid ${theme.colors.placeholderGray}`};
	padding: ${theme.spacing.xs} ${theme.spacing.sm};
	resize: none;
	width: 100%;

	&:focus {
		outline: ${({ borderColor = 'black' }) => `1px solid ${theme.colors[borderColor]}`};
	}

	&::placeholder {
		color: ${theme.tints.darken40(theme.colors.placeholderGray)};
	}
`;
